import React, { useState, useEffect } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Checkbox,
  TableSortLabel,
  useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import Pagination from '@mui/material/Pagination';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import moment from 'moment';
import UpdateIcon from '@mui/icons-material/Update';
import ORBApi from '../../../../services/ORBApi';

export default function PaymentFilesTable({
  tableData,
  onPageChange,
  onRateClick,
  onDeleteClick,
  onNotFoundTracksAlertClick,
  onMatchParcialTracksAlertClick,
  onUpdateStatusClick,
  hasRole,
  loadData,
  setLoading,
  onSortChange,
  order: propsOrder,
  orderBy: propsOrderBy,
}) {
  const theme = useTheme();
  const order = propsOrder || 'desc';
  const orderBy = propsOrderBy || 'month';
  
  // Estado para armazenar validações de taxas de câmbio por report
  const [ratesValidation, setRatesValidation] = useState({});

  // Mapeamento entre headers e campos dos dados
  // IMPORTANTE: Os nomes aqui devem corresponder aos campos do backend
  const getFieldName = (headerTitle) => {
    const headerMap = {
      'Data': 'month',
      'Arquivo': 'file',
      'Fonte': 'source',
      'Status': 'status',
      'Total registros': 'total_rows',
      'Id': 'id',
      'Câmbio': null, // Não ordenável (array de rates)
      'Mostrar taxas de câmbio': null, // Não ordenável (checkbox)
      'Remover': null, // Não ordenável (botão)
    };
    return headerMap[headerTitle] || null;
  };

  // Handler para mudança de ordenação - comunica ao componente pai
  const handleRequestSort = (property) => {
    if (!onSortChange) return;
    
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    const newOrderBy = property;
    
    onSortChange(newOrderBy, newOrder);
  };

  const clickHandler = (report_id, showExchangeRate) => {
    setLoading(1);
    let body = { show_exchange_rate: showExchangeRate };
    ORBApi.updatePaymentDetails(report_id, body)
      .then(() => {
        toast.success('Arquivo de pagamento atualizado');
        loadData(tableData.current_page);
      })
      .catch((error) => {
        toast.error('Erro ao atualizar arquivo de pagamento');
      })
      .finally(() => {
        setLoading(0);
      });
  };

  // Carregar validações de taxas quando os reports mudarem
  useEffect(() => {
    if (!tableData?.reports || tableData.reports.length === 0) {
      return;
    }

    // Carregar currencies para cada report com status 'Loaded'
    const loadValidations = async () => {
      const validations = {};
      
      for (const report of tableData.reports) {
        if (report.status === 'Loaded') {
          try {
            const currencies = await ORBApi.loadPaymentReportCurrencies(report.id);
            
            if (!currencies || !Array.isArray(currencies)) {
              validations[report.id] = false;
              continue;
            }

            // Filtrar moedas diferentes de BRL
            const nonBRLCurrencies = currencies.filter(
              (currency) => currency && currency.currency && currency.currency !== 'BRL'
            );

            // Se não houver moedas não-BRL cadastradas, considerar como INVÁLIDO por padrão
            // Isso é mais seguro porque arquivos como Universal2 geralmente precisam de outras moedas
            // que podem ainda não ter sido cadastradas. Só consideramos válido se houver evidência
            // explícita de que o arquivo realmente só precisa de BRL (ex: report.rates contém apenas BRL)
            if (nonBRLCurrencies.length === 0) {
              // Verificar se report.rates indica explicitamente que só há BRL
              const reportHasOnlyBRL = report.rates && Array.isArray(report.rates) && 
                report.rates.length > 0 &&
                report.rates.every((rate) => {
                  const currency = rate?.currency || rate;
                  return !currency || currency === 'BRL';
                });

              // Só considerar válido se report.rates indicar explicitamente que só há BRL
              if (reportHasOnlyBRL) {
                validations[report.id] = true;
                continue;
              }
              
              // Por padrão, se não há moedas não-BRL cadastradas, considerar como inválido
              // (mais seguro - força o usuário a cadastrar as moedas necessárias)
              validations[report.id] = false;
              continue;
            }

            // Verificar se todas as moedas (exceto BRL) têm taxa válida
            const allRatesFilled = nonBRLCurrencies.every((currency) => {
              const rateValue = currency.exchange_rate;
              if (rateValue === null || rateValue === undefined || rateValue === '') {
                return false;
              }
              const parsedRate = parseFloat(rateValue);
              return !isNaN(parsedRate) && parsedRate > 0;
            });

            validations[report.id] = allRatesFilled;
          } catch (error) {
            // Em caso de erro, considerar como não preenchido por segurança
            validations[report.id] = false;
          }
        } else {
          validations[report.id] = false;
        }
      }

      setRatesValidation(validations);
    };

    loadValidations();
  }, [tableData?.reports]);

  // Função helper para verificar se todas as taxas de câmbio estão preenchidas
  const hasAllExchangeRatesFilled = (report) => {
    // Retornar o valor do estado de validação, ou false se não estiver carregado ainda
    return ratesValidation[report.id] === true;
  };

  const buildRateTag = (report) => {
    return (
      <>
        {report.status === 'Loaded' &&
        hasRole(['PAYMENT_FILE_TRACKS_WRITE']) ? (
          <MonetizationOnIcon
            color="secondary"
            className="clickable"
            onClick={() => {
              onRateClick(report);
            }}
          />
        ) : null}
        {report.rates.map((rate) => {
          if (rate !== 'BRL') {
            return (
              <div key={rate.currency}>
                {rate.currency}={rate.rate}
              </div>
            );
          }
          return null;
        })}
      </>
    );
  };

  return (
    <>
      <TableContainer className="table-container" component={Paper}>
        <Table size="small" className="basic-table ">
          <TableHead className="basic-table-head">
            <TableRow>
              {['Data', 'Arquivo', 'Fonte', 'Status', 'Total registros', 'Câmbio', 'Mostrar taxas de câmbio', 'Remover', ''].map((title, idx) => {
                const fieldName = getFieldName(title);
                const isSortable = fieldName !== null;
                
                return (
                  <TableCell
                    key={idx}
                    align={title === 'Total registros' || title === 'Mostrar taxas de câmbio' || title === 'Remover' ? 'center' : 'left'}
                    style={{ color: '#F5F5F5' }}
                    sortDirection={orderBy === fieldName ? order : false}
                  >
                    {isSortable ? (
                      <TableSortLabel
                        active={orderBy === fieldName}
                        direction={orderBy === fieldName ? order : 'asc'}
                        onClick={() => handleRequestSort(fieldName)}
                        sx={{
                          '&.Mui-active': {
                            color: theme.palette.secondary.main,
                          },
                          '& .MuiTableSortLabel-icon': {
                            color: orderBy === fieldName 
                              ? `${theme.palette.secondary.main} !important` 
                              : 'rgba(255, 255, 255, 0.54)',
                          },
                          '&:hover': {
                            color: theme.palette.secondary.light,
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            color: `${theme.palette.secondary.light} !important`,
                          },
                          color: orderBy === fieldName 
                            ? theme.palette.secondary.main 
                            : 'inherit',
                        }}
                      >
                        {title}
                      </TableSortLabel>
                    ) : (
                      title
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.reports?.length > 0 &&
            hasRole(['PAYMENT_FILE_TRACKS_WRITE', 'PAYMENT_FILE_TRACKS_READ'])
              ? tableData.reports.map((report, index) => {
                  return (
                    <TableRow className="row" key={report.id}>
                      <TableCell style={{ margin: '5px', width: '10%' }}>
                        {moment(report.month).format('MM/YYYY')}
                      </TableCell>
                      <TableCell style={{ width: '40%' }}>
                        {report.file}
                        {report.status === 'Error' && report.error_message ? (
                          <div className="alert-notfound-tracks">
                            <Tooltip title={report.error_message} arrow>
                              <InfoRoundedIcon fontSize="small" color="secondary" />
                            </Tooltip>
                          </div>
                        ) : null}
                        {report.has_not_found_tracks ? (
                          <div
                            className="alert-notfound-tracks clickable"
                            onClick={() => {
                              onNotFoundTracksAlertClick(report);
                            }}
                          >
                            <Tooltip title="Possui faixas não cadastradas">
                              <ErrorRoundedIcon
                                fontSize="small"
                                color="secondary"
                              />
                            </Tooltip>
                          </div>
                        ) : null}
                        {report.has_parcial_matches ? (
                          <div
                            className="alert-notfound-tracks clickable"
                            onClick={() => {
                              onMatchParcialTracksAlertClick(report);
                            }}
                          >
                            <Tooltip title="Possui faixas com match parcial">
                              <ErrorRoundedIcon
                                fontSize="small"
                                color="secondary"
                              />
                            </Tooltip>
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell style={{ width: '10%' }}>
                        {report.source}
                      </TableCell>
                      <TableCell style={{ width: '5%' }}>
                        {report.status}
                      </TableCell>
                      <TableCell style={{ textAlign: 'center', width: '10%' }}>
                        {(() => {
                          const totalRows = report.total_rows || 0;
                          const processedRows = report.processed_rows || 0;
                          const progressPercent = report.progress_percent || 0;
                          
                          if (totalRows > 0) {
                            return (
                              <Tooltip title={`${processedRows.toLocaleString('pt-BR')} / ${totalRows.toLocaleString('pt-BR')} registros`}>
                                <div>
                                  <div>
                                    {totalRows.toLocaleString('pt-BR')}
                                  </div>
                                  {progressPercent > 0 && progressPercent < 100 && (
                                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                      {Number(progressPercent).toFixed(1)}%
                                    </div>
                                  )}
                                </div>
                              </Tooltip>
                            );
                          } else if (processedRows > 0) {
                            return (
                              <Tooltip title={`${processedRows.toLocaleString('pt-BR')} registros processados`}>
                                <div>
                                  {processedRows.toLocaleString('pt-BR')}
                                </div>
                              </Tooltip>
                            );
                          } else {
                            return <span style={{ color: '#999' }}>-</span>;
                          }
                        })()}
                      </TableCell>
                      <TableCell style={{ textAlign: 'center', width: '5%' }}>
                        {buildRateTag(report)}
                      </TableCell>
                      <TableCell style={{ width: '30%', textAlign: 'center' }}>
                        {report.show_exchange_rate ? (
                          <Checkbox
                            checked={true}
                            onChange={(e) => {
                              clickHandler(
                                report.id,
                                !report.show_exchange_rate,
                              );
                            }}
                          />
                        ) : (
                          <Checkbox
                            checked={false}
                            onChange={(e) => {
                              clickHandler(
                                report.id,
                                !report.show_exchange_rate,
                              );
                            }}
                          />
                        )}
                      </TableCell>
                      {hasRole(['PAYMENT_FILE_TRACKS_WRITE']) ? (
                        <>
                          <TableCell
                            style={{ width: '10%', textAlign: 'center' }}
                            className={report.status !== 'Distributed' ? 'clickable' : ''}
                            onClick={report.status !== 'Distributed' ? () => {
                              onDeleteClick(report);
                            } : undefined}
                          >
                            {report.status !== 'Distributed' ? (
                              <CancelIcon color="secondary" />
                            ) : null}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell></TableCell>
                      )}
                      {report.status === 'Loaded' && hasAllExchangeRatesFilled(report) ? (
                        <TableCell>
                          <span
                            onClick={() => onUpdateStatusClick(report)}
                            className="update-icon"
                          >
                            <UpdateIcon fontSize="small" />
                            Ready
                          </span>
                        </TableCell>
                      ) : (
                        <TableCell></TableCell>
                      )}
                    </TableRow>
                  );
                })
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        shape="rounded"
        className="table-pagination"
        count={tableData.pages}
        page={tableData.current_page}
        onChange={(e, value) => {
          onPageChange(value);
        }}
      />
    </>
  );
}
